import { createBrowserRouter } from 'react-router-dom';
import RouteErrorBoundary from '@/shared/components/error/RouteErrorBoundary.jsx';
import Layout from '@/layout/Layout.jsx';
import { authLoader, guestLoader } from '@/router/loaders/authLoader.js';

import RouteFallback from '@/shared/components/loading/RouteFallback.jsx';
import SimpleFallback from '@/shared/components/loading/SimpleFallback.jsx';
import AuthFallback from '@/shared/components/loading/AuthFallback.jsx';
import { FEATURE_FLAGS } from '@/shared/config/featureFlags.js';

const lazyRoute = (importFn) => async () => {
  const module = await importFn();
  return { Component: module.default };
};

const loadDeviceAnalyticsPage = FEATURE_FLAGS.deviceAnalyticsV2
  ? () => import('@/feature/Devices/pages/AnalyticsV2EntryPage.jsx')
  : () => import('@/feature/Devices/pages/AnalyticsPage.jsx');

const router = createBrowserRouter([
  // Auth Routes
  {
    path: '/login',
    lazy: lazyRoute(() => import('@/feature/Auth/pages/LoginPage.jsx')),
    HydrateFallback: AuthFallback,
    loader: guestLoader,
  },
  {
    path: '/signup',
    lazy: lazyRoute(() => import('@/feature/Auth/pages/SignupPage.jsx')),
    HydrateFallback: AuthFallback,
    loader: guestLoader,
  },
  {
    path: '/verify-otp',
    lazy: lazyRoute(() => import('@/feature/Auth/pages/VerifyOTPPage.jsx')),
    HydrateFallback: AuthFallback,
    loader: guestLoader,
  },
  {
    path: '/forgot-password',
    lazy: lazyRoute(
      () => import('@/feature/Auth/pages/ForgotPasswordPage.jsx'),
    ),
    HydrateFallback: AuthFallback,
    loader: guestLoader,
  },

  // Main App Routes
  {
    path: '/',
    element: <Layout />,
    loader: authLoader,
    errorElement: <RouteErrorBoundary />,
    HydrateFallback: RouteFallback,
    children: [
      {
        index: true,
        lazy: lazyRoute(() => import('@/feature/Home/pages/HomePage.jsx')),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'devices/:deviceEui/analytics',
        lazy: lazyRoute(loadDeviceAnalyticsPage),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'tenant',
        lazy: lazyRoute(() => import('@/feature/Tenant/pages/TenantPage.jsx')),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'users',
        lazy: lazyRoute(
          () => import('@/feature/Users/pages/TenantMembersPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'gateway',
        lazy: lazyRoute(
          () => import('@/feature/Gateway/pages/GatewayPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'devices',
        lazy: lazyRoute(
          () => import('@/feature/Devices/pages/DevicesPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'dashboard',
        lazy: lazyRoute(
          () => import('@/feature/Dashboard/pages/DashboardSelectorPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'rules',
        lazy: lazyRoute(() => import('@/feature/Rules/pages/RulesPage.jsx')),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'rules/create',
        lazy: lazyRoute(
          () => import('@/feature/Rules/pages/CreateRulePage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'rules/:ruleId',
        lazy: lazyRoute(
          () => import('@/feature/Rules/pages/RuleDetailPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'rules/message/:messageId',
        lazy: lazyRoute(
          () => import('@/feature/Rules/pages/MessageDetailPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'billing',
        lazy: lazyRoute(
          () => import('@/feature/Billing/pages/BillingCallbackPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'payment-result',
        lazy: lazyRoute(
          () => import('@/feature/Billing/pages/PaymentResultPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'subscription',
        lazy: lazyRoute(
          () => import('@/feature/Billing/pages/SubscriptionPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'credit',
        lazy: lazyRoute(() => import('@/feature/Billing/pages/CreditPage.jsx')),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'invoices',
        lazy: lazyRoute(
          () => import('@/feature/Billing/pages/InvoicesPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'payments',
        lazy: lazyRoute(
          () => import('@/feature/Billing/pages/PaymentsPage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
      {
        path: 'profile/:tab',
        lazy: lazyRoute(
          () => import('@/feature/Profile/pages/ProfilePage.jsx'),
        ),
        HydrateFallback: RouteFallback,
      },
    ],
  },

  // 404 Route
  {
    path: '*',
    lazy: lazyRoute(() => import('@/feature/Errors/pages/NotFoundPage.jsx')),
    HydrateFallback: SimpleFallback,
  },
]);

export default router;
